import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Tab from 'UIKit/Tab'
import Tabs from 'UIKit/Tabs'
import Text from 'UIKit/Text'
import { RowBetween } from 'layout/Components/Row'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import theme from 'theme'
import {
  InHouseGameNumericalInput,
  InHouseLabel,
  InHouseTab,
  InhouseGameInputContainer,
} from 'views/InhouseGame/components/styled'
import { FinishBetConfiguration } from 'views/InhouseGame/config/autobet'

enum TriggerType {
  Reset = 0,
  Increase = 1,
}

const BetAmountTrigger: React.FC<
  {
    disabled?: boolean
    config: FinishBetConfiguration
    setConfig: (config: FinishBetConfiguration) => void
    label: string
  } & BoxProps
> = ({ disabled, label, config, setConfig, ...props }) => {
  const { t } = useTranslation()

  const tab = config.isReset ? TriggerType.Reset : TriggerType.Increase

  return (
    <Flex flexDirection="column" {...props}>
      <RowBetween>
        <InHouseLabel>{t(label)}</InHouseLabel>
      </RowBetween>

      <StyledContainer $disabled={disabled}>
        <StyledTriggerTypeTabs
          value={tab}
          onTagChanged={(tab) => {
            if (!disabled) {
              setConfig({ ...config, isReset: tab === TriggerType.Reset })
            }
          }}
          backgroundBlurColor={theme.colors.background}
          indicator={<StyledIndicator />}
          $disabled={disabled}
        >
          <StyledTriggerTypeTab value={TriggerType.Reset} $active={tab === TriggerType.Reset}>
            <Trans>Reset</Trans>
          </StyledTriggerTypeTab>
          <StyledTriggerTypeTab value={TriggerType.Increase} $active={tab === TriggerType.Increase}>
            <Trans>Increase by</Trans>
          </StyledTriggerTypeTab>
        </StyledTriggerTypeTabs>

        <StyledInhouseGameInputContainer disabled width="100%" height="38px" ml="4px">
          <InHouseGameNumericalInput
            disabled={disabled || tab === TriggerType.Reset}
            placeholder="0"
            pl="12px"
            autoFocus={false}
            tabIndex={1}
            value={config.increasePercent}
            onValueChanged={(e) => {
              setConfig({ ...config, increasePercent: e.split('.')?.[0] ? e : e })
            }}
            validateOnchange
          />
          <Text p="12px" bold fontSize="12px">
            %
          </Text>
        </StyledInhouseGameInputContainer>
      </StyledContainer>
    </Flex>
  )
}

const StyledInhouseGameInputContainer = styled(InhouseGameInputContainer)`
  border: 2px solid transparent;
  &:hover {
    border: 2px solid ${({ theme }) => theme.colors.strokeAlt} !important;
  }
`

const StyledContainer = styled(Flex)<{ $disabled?: boolean }>`
  border-radius: ${({ theme }) => theme.radii.tiny};
  background: rgba(255, 255, 255, 0.04);
  height: 42px;

  border-radius: ${({ theme }) => theme.radii.tiny};
  border: 2px solid transparent;
  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      * {
        pointer-events: none;
      }
    `}
`
const StyledTriggerTypeTabs = styled(Tabs)<{ $disabled?: boolean }>`
  border-radius: ${({ theme }) => theme.radii.tiny};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
      user-select: none;
      cursor: not-allowed;

      ${InHouseTab} {
        user-select: none;
        cursor: not-allowed;
      }
    `}
`

const StyledTriggerTypeTab = styled(Tab)<{ $active: boolean }>`
  padding: 0px 10px;
  height: 36px;
  line-height: 16px;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;

  transition: ${({ theme }) => theme.transitions.fast};
  color: ${({ theme, $active }) => ($active ? `${theme.colors.text}` : theme.colors.textSubtle)};
  white-space: pre !important;
  font-weight: 500;
  font-size: 12px;
`

const StyledIndicator = styled(Box)`
  border-radius: ${({ theme }) => theme.radii.tiny};
  background: ${({ theme }) => theme.colors.inputBackground};

  height: 100%;
  width: 100%;
`

export default BetAmountTrigger
